import { createTheme } from '@material-ui/core';

declare module '@material-ui/core/styles/createTheme' {
	interface Theme {
		sidebarWidth: number;
		sidebarMobileHeight: number;
		brand: string;
		footerHeight: number;
		mobileFooterHeight: number;
		sidebarMobilePadding: number;
		participantBorderWidth: number;
		rightDrawerWidth: number;
		galleryViewBackgroundColor: string;
	}

	// allow configuration using `createMuiTheme`
	interface ThemeOptions {
		sidebarWidth?: number;
		sidebarMobileHeight?: number;
		brand: string;
		footerHeight: number;
		mobileFooterHeight: number;
		sidebarMobilePadding: number;
		participantBorderWidth: number;
		rightDrawerWidth?: number;
		galleryViewBackgroundColor: string;
	}
}

const defaultTheme = createTheme();

export const colors = {
	pageBackground: '#F4F6F8',
	surface: '#FFFFFF',
	text: '#1F2933',
	textSecondary: '#52606D',
	border: '#D9DEE3',
	borderStrong: '#8A94A0',
	primary: '#027AC5',
	primaryDark: '#01598F',
	danger: '#D93025',
	dangerDark: '#A8231B',
};

export default createTheme({
	shape: {
		borderRadius: 8,
	},
	typography: {
		fontFamily:
			'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
		fontSize: 16,
		htmlFontSize: 16,
		h5: {
			fontSize: '1.5rem',
			fontWeight: 600,
			lineHeight: 1.3,
		},
		h6: {
			fontSize: '1.125rem',
			fontWeight: 600,
			lineHeight: 1.4,
		},
		subtitle1: {
			fontSize: '1rem',
			fontWeight: 600,
			lineHeight: 1.5,
		},
		subtitle2: {
			fontSize: '1rem',
			fontWeight: 600,
		},
		body1: {
			fontSize: '1rem',
			lineHeight: 1.5,
		},
		body2: {
			fontSize: '0.9375rem',
			lineHeight: 1.5,
		},
		button: {
			fontSize: '1rem',
			fontWeight: 500,
			lineHeight: 1.5,
			textTransform: 'none',
		},
	},
	palette: {
		primary: {
			main: colors.primary,
			dark: colors.primaryDark,
		},
		error: {
			main: colors.danger,
		},
		text: {
			primary: colors.text,
			secondary: colors.textSecondary,
		},
		divider: colors.border,
		background: {
			default: colors.pageBackground,
			paper: colors.surface,
		},
		action: {
			disabled: 'rgba(31, 41, 51, 0.45)',
		},
	},
	overrides: {
		MuiCssBaseline: {
			'@global': {
				'html, body, #root': {
					height: '100%',
				},
				body: {
					WebkitFontSmoothing: 'antialiased',
					MozOsxFontSmoothing: 'grayscale',
				},
			},
		},
		MuiButton: {
			root: {
				minHeight: 44,
				padding: '8px 16px',
				color: colors.text,
				transition: defaultTheme.transitions.create(
					['background-color', 'box-shadow', 'border', 'color'],
					{
						duration: defaultTheme.transitions.duration.short,
					},
				),
				'&$focusVisible': {
					boxShadow: '0 0 0 3px rgba(2, 122, 197, 0.45)',
				},
			},
			text: {
				padding: '8px 14px',
			},
			contained: {
				boxShadow: 'none',
				'&:hover': {
					boxShadow: 'none',
				},
			},
			outlined: {
				borderWidth: 2,
				padding: '6px 16px',
				'&:hover': {
					borderWidth: 2,
				},
			},
			outlinedPrimary: {
				border: `2px solid ${colors.primary}`,
				'&:hover': {
					border: `2px solid ${colors.primaryDark}`,
				},
			},
			startIcon: {
				marginRight: 8,
			},
		},
		MuiIconButton: {
			root: {
				color: colors.text,
			},
		},
		MuiTypography: {
			body1: {
				color: colors.text,
			},
		},
		MuiInputBase: {
			root: {
				fontSize: '1rem',
			},
		},
		MuiOutlinedInput: {
			root: {
				background: colors.surface,
			},
			input: {
				padding: '14px 16px',
			},
			notchedOutline: {
				borderColor: colors.borderStrong,
			},
		},
		MuiSelect: {
			root: {
				padding: '0.85em',
			},
		},
		MuiMenuItem: {
			root: {
				minHeight: 48,
				fontSize: '1rem',
			},
		},
		MuiDialogActions: {
			root: {
				padding: '16px',
			},
		},
		MuiTextField: {
			root: {
				color: colors.text,
			},
		},
		MuiInputLabel: {
			root: {
				color: colors.text,
				fontSize: '1rem',
				marginBottom: '0.4em',
				fontWeight: 500,
			},
		},
		MuiSwitch: {
			root: {
				width: 42,
				height: 18,
				padding: 0,
				display: 'flex',
				marginRight: '0.5em',
			},
			switchBase: {
				padding: 2,
				color: '#FFFFFF',
				'&$checked': {
					transform: 'translateX(18px)',
					top: '50%',
					marginTop: -24 / 2,
					'&$disabled': {
						'& + $track': {
							opacity: '0.5',
						},
					},
				},
			},
			colorSecondary: {
				'&$checked': {
					// Controls checked color for the thumb
					color: 'FFFFF',
				},
			},
			thumb: {
				width: 14,
				height: 14,
				boxShadow: 'none',
			},
			track: {
				borderRadius: 16 / 2,
				opacity: 1,
				backgroundColor: '#E1E3EA',
				'$checked$checked + &': {
					opacity: 1,
					backgroundColor: '#14B053',
				},
			},
		},
	},
	brand: colors.danger,
	footerHeight: 76,
	mobileFooterHeight: 68,
	sidebarWidth: 300,
	sidebarMobileHeight: 90,
	sidebarMobilePadding: 8,
	participantBorderWidth: 2,
	rightDrawerWidth: 320,
	galleryViewBackgroundColor: '#121C2D',
});
